import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from '../utils/banbox_axios';
import { getCookie } from '../utils/helper/Helper';
import { toast } from '@ravenpay/raven-bank-ui';
import defaultAxios, { AxiosError } from 'axios';
import env from '../env';

export interface VerifyCACPayload {
	rc_number: string;
	business_id: any;
	email: string;
	cac_document: File;
	cac_status_report: File;
	memart: File;
}

export interface Business {
	id: any;
	approved_name: string;
	email: string;
	poseidon_business_name: string;
	poseidon_email: string;
}

interface SearchParams {
	email?: string;
	name?: string;
}

export type ValidateCACResponse = BusinessData[];

export interface BusinessData {
	id: number;
	rc_number: string;
	business_category: string;
	approved_name: string;
	poseidon_business_name?: string;
	poseidon_email?: string;
	// ... other fields
}

type CacPayload =
	| FormData
	| {
			rc_number: string;
			business_id: string;
			email: string;
			cac_document: string;
			cac_status_report: string;
			memart: string;
	  };

interface VerifyCACResponse {
	success: boolean;
	message: string;
	data: {
		rc_number: string;
		email: string;
		approved_name: string;
		address: string;
		classification_id: string;
		city: string;
		state: string;
		lga: string;
		registration_date: string;
		business_commencement_date: string | null;
		delisting_status: string | null;
		company_type_name: string | null;
		nature_of_business_name: string | null;
		registration_approved: boolean;
		head_office_address: string | null;
		objectives: string | null;
	};
}

export const verifyCACDocuments = createAsyncThunk(
	'cac/verifyDocuments',
	async (payload: CacPayload, thunkAPI) => {
		try {
			const isFormData = payload instanceof FormData;

			const { data } = await defaultAxios.post(
				`${env.bankbox_api}atlas/pdon/verification/cac`,
				payload,
				{
					headers: {
						Authorization: `Bearer ${getCookie('token') || ''}`,
						'x-pos-key': getCookie('x-pos-key') || '',
						...(isFormData ? {} : { 'Content-Type': 'application/json' }),
					},
				}
			);

			if (data?.status === 'fail') {
				if (typeof data?.data === 'string') {
					toast.error(data?.data);
				} else {
					toast.error(data?.message);
				}
				return thunkAPI.rejectWithValue(data);
			}

			if (data?.status === 'success') {
				toast.success(data?.data?.message, {
					position: 'top-right',
				});

				return data;
			}
		} catch (error: any) {
			if (error.message === 'Network Error') {
				// toast.error('Network error');
			}
			if (
				error.response?.data?.status === 'fail' &&
				error.response.status !== 401
			) {
				return thunkAPI.rejectWithValue(error);
			}
		}
	}
);

// Validate RC Number API Call
export const validateCACRcNumber = createAsyncThunk<
	ValidateCACResponse,
	Pick<VerifyCACPayload, 'rc_number'>,
	{ rejectValue: any }
>('cac/validateCAC', async (payload, thunkAPI) => {
	try {
		const res = await axios.get(`/atlas/pdon/merchant/validate_cac`, {
			params: { rc_number: payload.rc_number },
			headers: {
				'x-pos-key': getCookie('x-pos-key') || '',
			},
		});

		if (res.data?.status === 'fail') {
			const message =
				typeof res.data?.data === 'string' ? res.data.data : res.data.message;
			// toast.error(message);
			return thunkAPI.rejectWithValue(message);
		}

		return res.data?.data?.data;
	} catch (error: any) {
		const message = error?.response?.data?.message || 'Something went wrong';
		// toast.error(message);
		return thunkAPI.rejectWithValue(message);
	}
});

export const searchMerchantsByEmail = createAsyncThunk<
	Business[],
	SearchParams,
	{ rejectValue: string }
>('cac/searchMerchantsByEmail', async ({ email, name }, thunkAPI) => {
	try {
		const params: { search?: string } = {};
		if (email) {
			params.search = email;
		} else if (name) {
			params.search = name;
		}
		const res = await axios.get(`/atlas/pdon/search_merchants`, {
			params: params,
			headers: {
				Authorization: `Bearer ${getCookie('token') || ''}`,
				'x-pos-key': getCookie('x-pos-key') || '',
			},
		});

		//(res);

		if (res.data?.status === 'fail') {
			const message = res.data.message || 'Search failed';
			return thunkAPI.rejectWithValue(message);
		}
		//('api', res.data?.data?.data);
		return res.data?.data?.data;
	} catch (error: any) {
		const message = error?.response?.data?.message || 'Something went wrong';
		return thunkAPI.rejectWithValue(message);
	}
});

export interface CacState {
	loading: boolean;
	successMessage: string | null;
	errorMessage: string | null;
	validationResult: ValidateCACResponse | null;
	searchResults: Business[];
}

const initialState: CacState = {
	loading: false,
	successMessage: null,
	searchResults: [],
	errorMessage: null,
	validationResult: null,
};

const cacSlice = createSlice({
	name: 'cac',
	initialState,
	reducers: {
		clearCacMessages: (state) => {
			state.successMessage = null;
			state.errorMessage = null;
		},
	},
	extraReducers: (builder) => {
		builder.addCase(verifyCACDocuments.pending, (state) => {
			state.loading = true;
			state.errorMessage = null;
			state.successMessage = null;
		});

		builder.addCase(verifyCACDocuments.fulfilled, (state, action) => {
			state.loading = false;
			state.successMessage = 'Documents verified successfully';
			state.errorMessage = null;
		});

		builder.addCase(verifyCACDocuments.rejected, (state, action) => {
			state.loading = false;
			state.errorMessage = action.payload as string;
		});

		builder.addCase(validateCACRcNumber.pending, (state, action) => {
			state.loading = true;
			state.errorMessage = null;
			state.validationResult = null;
		});

		builder.addCase(validateCACRcNumber.fulfilled, (state, action) => {
			state.loading = false;
			state.validationResult = action.payload;
		});

		builder.addCase(validateCACRcNumber.rejected, (state, action) => {
			state.loading = false;
			state.errorMessage = action.payload as string;
			state.validationResult = null;
		});
		builder.addCase(searchMerchantsByEmail.fulfilled, (state, action) => {
			state.searchResults = action.payload;
		});

		builder.addCase(searchMerchantsByEmail.rejected, (state, action) => {
			state.errorMessage = action.payload as string;
		});
	},
});

export const { clearCacMessages } = cacSlice.actions;
export default cacSlice.reducer;
